import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Button, Flex, FormInstance, ModalProps } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { getScheduleCreateModalContainer, SCHEDULE_CREATE_MODAL_ROOT_CLASS_NAME } from '../../utils/modal';
import {
  IconContainer,
  ScheduleCreateModalFormItem,
  ScheduleNameInput,
  ScheduleNameInputEditable,
  ScheduleNameText,
} from './ScheduleCreateModal.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';

interface UseScheduleCreateModalModalConfigProps {
  handleCancel: () => void;
  isScheduleCreateLoading: boolean;
  isScheduleUpdateLoading: boolean;
  hasScheduleChanges: boolean;
  handleValidateFields: (fields?: string[]) => void;
  handleSaveChanges: () => void;
  form: FormInstance;
  robot: Machine;
  scheduleName?: string;
  isEditingScheduleName: boolean;
  isScheduleNameInvalid: boolean;
  handleToggleEditingScheduleName: () => void;
}

export const useScheduleCreateModalModalConfig = ({
  handleCancel,
  isScheduleCreateLoading,
  isScheduleUpdateLoading,
  hasScheduleChanges,
  handleValidateFields,
  handleSaveChanges,
  form,
  robot,
  scheduleName,
  isEditingScheduleName,
  isScheduleNameInvalid,
  handleToggleEditingScheduleName,
}: UseScheduleCreateModalModalConfigProps): ModalProps => {
  const { t } = useTranslation();

  const {
    isCreateModalOpen,
    isEditModalOpen,
    isScheduleCreateFullSizeOpen,
    isReadOnly,
    handleAfterCloseModal,
    handleOpenDeleteModal,
  } = useMachineDetailsRobotSchedule();

  const renderScheduleNameField = useCallback(
    ({ title, isEdit }: { title: string; isEdit: boolean }): JSX.Element => {
      if (!isEdit && isScheduleCreateFullSizeOpen) {
        return (
          <ScheduleNameInput
            defaultValue={title}
            variant="borderless"
            placeholder={t('common.addTitle')}
            disabled={isScheduleCreateFullSizeOpen || isReadOnly}
            autoFocus={!isScheduleCreateFullSizeOpen && !isEdit}
          />
        );
      }

      const isNameFieldReadOnly = isEdit && !isEditingScheduleName;

      // Always mounted (hidden when read-only) so the Form field never unregisters.
      const nameFormItem = (
        <ScheduleCreateModalFormItem
          name="scheduleName"
          className={classnames({
            'schedule-name-edit-field': isEdit,
            'schedule-name-field-hidden': isNameFieldReadOnly,
            'schedule-name-field-visible': !isNameFieldReadOnly,
          })}
          rules={[{ required: true, message: t('createSchedule.scheduleNameRequired') }]}
          initialValue={isEdit ? title : undefined}
        >
          {isEdit ? (
            <ScheduleNameInputEditable
              placeholder={t('common.addTitle')}
              disabled={isScheduleCreateFullSizeOpen || isReadOnly}
              tabIndex={isNameFieldReadOnly ? -1 : 0}
              autoFocus={isEditingScheduleName}
              onChange={(): void => handleValidateFields(['scheduleName'])}
              onPressEnter={(event): void => {
                event.preventDefault();
                if (!isScheduleNameInvalid) {
                  handleToggleEditingScheduleName();
                }
              }}
            />
          ) : (
            <ScheduleNameInput
              variant="borderless"
              placeholder={t('common.addTitle')}
              disabled={isScheduleCreateFullSizeOpen || isReadOnly}
              autoFocus={!isScheduleCreateFullSizeOpen}
              onChange={(): void => handleValidateFields(['scheduleName'])}
            />
          )}
        </ScheduleCreateModalFormItem>
      );

      if (isNameFieldReadOnly) {
        return (
          <>
            {nameFormItem}
            <ScheduleNameText title={title}>{title}</ScheduleNameText>
          </>
        );
      }

      return nameFormItem;
    },
    [
      t,
      isScheduleCreateFullSizeOpen,
      isReadOnly,
      handleValidateFields,
      isEditingScheduleName,
      isScheduleNameInvalid,
      handleToggleEditingScheduleName,
    ]
  );

  const renderTitleModal = useCallback(
    ({ title, isEdit = false }: { title: string; isEdit?: boolean }): JSX.Element => {
      const titleRow = (
        <Flex align={isEdit ? 'center' : 'flex-start'} justify="space-between" gap={kccThemeConstants.SPACING.SM}>
          <Flex align="center" gap={kccThemeConstants.SPACING.XS} style={{ flex: 1, minWidth: 0 }}>
            {renderScheduleNameField({ title, isEdit })}

            {isEdit && !isReadOnly && (
              <Button
                type="link"
                aria-label={isEditingScheduleName ? t('common.confirm') : t('common.edit')}
                icon={
                  <SvgIcon name={isEditingScheduleName ? 'check' : 'editNew'} className="schedule-name-edit-icon" />
                }
                disabled={isEditingScheduleName && isScheduleNameInvalid}
                onClick={handleToggleEditingScheduleName}
                style={{ width: kccThemeConstants.SIZE.S250, height: kccThemeConstants.SIZE.S250, flexShrink: 0 }}
              />
            )}
          </Flex>

          <Button
            type="link"
            aria-label={t('common.close')}
            onClick={handleCancel}
            disabled={false}
            style={{ width: kccThemeConstants.SIZE.S300, height: kccThemeConstants.SIZE.S300, flexShrink: 0 }}
          >
            <IconContainer>
              <SvgIcon name="close" />
            </IconContainer>
          </Button>
        </Flex>
      );

      if (!isEdit) {
        return titleRow;
      }

      return (
        <Flex vertical style={{ position: 'relative' }}>
          {titleRow}
          {isEditingScheduleName && <div style={{ height: kccThemeConstants.SIZE.S200 }} />}
        </Flex>
      );
    },
    [
      handleCancel,
      t,
      isReadOnly,
      isEditingScheduleName,
      isScheduleNameInvalid,
      handleToggleEditingScheduleName,
      renderScheduleNameField,
    ]
  );

  const baseModalConfig = useMemo(
    () => ({
      destroyOnClose: true,
      closable: false,
      centered: true,
      width: 800,
      afterClose: handleAfterCloseModal,
    }),
    [handleAfterCloseModal]
  );

  const createModalConfig = {
    ...baseModalConfig,
    title: renderTitleModal({
      title: t('createSchedule.title', { robotName: robot.name.toUpperCase() }),
      isEdit: false,
    }),
    open: isCreateModalOpen,
    footer: [
      <Button
        key="cancel"
        onClick={handleCancel}
        style={{ minWidth: '100px', width: 'fit-content' }}
        disabled={isScheduleCreateLoading}
      >
        {t('common.cancel')}
      </Button>,
      <Button
        key="create-schedule"
        type="primary"
        style={{ minWidth: '100px', width: 'fit-content' }}
        loading={isScheduleCreateLoading}
        onClick={(): void => {
          handleValidateFields();
          form.submit();
        }}
      >
        {t('common.createSchedule')}
      </Button>,
    ],
  };

  const editModalConfig = useMemo(
    () => ({
      ...baseModalConfig,
      title: renderTitleModal({ title: scheduleName || '-', isEdit: true }),
      open: isEditModalOpen,
      footer: [
        ...(!isReadOnly
          ? [
              <Button
                key="delete-schedule"
                type="link"
                style={{ minWidth: '100px', width: 'fit-content', padding: 0 }}
                icon={
                  <Flex align="center" justify="center">
                    <SvgIcon name="trash" />
                  </Flex>
                }
                onClick={handleOpenDeleteModal}
                disabled={isScheduleUpdateLoading}
              >
                {t('common.delete')}
              </Button>,
            ]
          : []),
        <Button
          key="cancel"
          onClick={handleCancel}
          style={{ minWidth: '100px', width: 'fit-content' }}
          disabled={isScheduleUpdateLoading}
        >
          {t('common.cancel')}
        </Button>,
        ...(!isReadOnly
          ? [
              <Button
                key="create-schedule"
                type="primary"
                style={{ minWidth: '100px', width: 'fit-content' }}
                loading={isScheduleUpdateLoading}
                onClick={handleSaveChanges}
                disabled={!hasScheduleChanges || isScheduleUpdateLoading}
              >
                {t('common.saveChanges')}
              </Button>,
            ]
          : []),
      ],
    }),
    [
      baseModalConfig,
      renderTitleModal,
      scheduleName,
      isEditModalOpen,
      handleCancel,
      handleOpenDeleteModal,
      hasScheduleChanges,
      handleSaveChanges,
      isScheduleUpdateLoading,
      isReadOnly,
      t,
    ]
  );

  const smallModalConfig = {
    mask: false,
    getContainer: getScheduleCreateModalContainer,
    rootClassName: SCHEDULE_CREATE_MODAL_ROOT_CLASS_NAME,
    width: 530,
  };

  if (isScheduleCreateFullSizeOpen) {
    return createModalConfig;
  }

  if (isEditModalOpen) {
    return { ...editModalConfig, ...smallModalConfig };
  }

  return { ...createModalConfig, ...smallModalConfig };
};
